// Deliverable-side wrapper around LifecycleTransitionDialog. Translates
// the derived lifecycle status into a state-aware outcome set, maps each
// outcome to its lifecycle event type, supplies the contextual comment
// label, and wires the failed-acceptance soft-warning condition. Pure
// — caller passes derivedStatus + failedAcceptanceCount; the dialog
// does not reach into a Deliverable model.

import { useMemo } from "react";
import LifecycleTransitionDialog, {
  type Outcome,
  type SoftWarningCtx,
} from "../shared/LifecycleTransitionDialog";

/** Mirrors backend LifecycleEventType in backend/src/models/tracking.ts —
 *  must stay in sync. Only the subset reachable as a target from the
 *  acceptance gate is enumerated here; `revision-requested`, `closed`
 *  and `reverted` are reached through other flows. */
export type DeliverableLifecycleTarget =
  | "submitted"
  | "under-review"
  | "conditionally-accepted"
  | "awaiting-resubmission"
  | "failed-acceptance"
  | "accepted"
  | "rejected";

/** Derived lifecycle status the dialog branches on. Matches the
 *  resting states the deliverable can be in at the moment the dialog
 *  is opened (terminal states render reopen-only). */
export type DeliverableDerivedLifecycleStatus =
  | "submitted"
  | "under-review"
  | "conditionally-accepted"
  | "awaiting-resubmission"
  | "accepted"
  | "rejected"
  | "closed";

export interface AcceptanceGateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  derivedStatus: DeliverableDerivedLifecycleStatus;
  /** Count of prior `failed-acceptance` events. Drives the
   *  contextual prior-failures line and the third-failure soft
   *  warning when fail-acceptance is the selected outcome. */
  failedAcceptanceCount: number;
  /** Earliest legal date for the new event — typically the date of
   *  the last lifecycle event. Server enforces the same invariant;
   *  the dialog mirrors it for fast feedback. */
  minDate?: string;
  /** Brisbane-today, injected for testability. */
  todayIso: string;
  onSubmit: (
    target: DeliverableLifecycleTarget,
    payload: { date: string; comment?: string },
  ) => Promise<unknown>;
  onReopen: () => Promise<unknown>;
}

// Ordinal suffix for small positive integers — covers the common cases
// the failed-acceptance soft warning hits (3rd, 4th, 5th, …) plus the
// teens carve-out (11th, 12th, 13th).
function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  const mod10 = n % 10;
  if (mod10 === 1) return `${n}st`;
  if (mod10 === 2) return `${n}nd`;
  if (mod10 === 3) return `${n}rd`;
  return `${n}th`;
}

const FAIL_SOFT_WARNING_THRESHOLD = 3;

function failAcceptanceSoftWarning(ctx: SoftWarningCtx): string | null {
  const count = ctx.failedAcceptanceCount ?? 0;
  const next = count + 1;
  if (next < FAIL_SOFT_WARNING_THRESHOLD) return null;
  return `This will be the ${ordinal(next)} failed acceptance — consider Reject?`;
}

// Builder helpers — keeps the outcome set declarations readable and
// pushes the lifecycle-event-type mapping into one place.
function acceptOutcome(id: "accept" | "mark-accepted"): Outcome {
  return {
    id,
    label: id === "accept" ? "Mark accepted" : "Mark accepted (conditions met)",
    pill: "active",
    pillLabel: "Accepted",
    description:
      "Full acceptance. Terminal — cannot be undone without re-opening.",
    requiresComment: false,
    targetState: "accepted",
  };
}

const conditionallyAcceptOutcome: Outcome = {
  id: "conditionally-accept",
  label: "Conditionally accept",
  pill: "outlinedAmber",
  pillLabel: "Conditionally accepted",
  description:
    "Accept subject to conditions. Resolves to Accepted once conditions are met.",
  requiresComment: true,
  commentLabel: "conditions",
  targetState: "conditionally-accepted",
};

const failAcceptanceOutcome: Outcome = {
  id: "fail-acceptance",
  label: "Fail acceptance",
  pill: "outlinedRose",
  pillLabel: "Failed",
  description:
    "Records a dated failure event (count + 1) and moves the deliverable to Awaiting resubmission. Reason required.",
  requiresComment: true,
  commentLabel: "reason",
  targetState: "failed-acceptance",
  softWarning: failAcceptanceSoftWarning,
};

const rejectOutcome: Outcome = {
  id: "reject",
  label: "Reject",
  pill: "filledRed",
  pillLabel: "Rejected",
  description: "Terminal failure. Reason required.",
  requiresComment: true,
  commentLabel: "reason",
  targetState: "rejected",
};

const returnToReviewOutcome: Outcome = {
  id: "return-to-review",
  label: "Return to review",
  pill: "outlinedAmber",
  pillLabel: "Under review",
  description:
    "Re-review the existing deliverable — no fresh submission required.",
  requiresComment: false,
  targetState: "under-review",
};

const sendBackToSupplierOutcome: Outcome = {
  id: "send-back-to-supplier",
  label: "Send back to supplier",
  pill: "outlinedRose",
  pillLabel: "Awaiting resubmission",
  description:
    "A fresh deliverable is required. Returns to the supplier-action queue.",
  requiresComment: false,
  targetState: "awaiting-resubmission",
};

const supplierResubmitsOutcome: Outcome = {
  id: "supplier-resubmits",
  label: "Supplier resubmits",
  pill: "outlinedAmber",
  pillLabel: "Submitted",
  description:
    "Record the fresh submission. Moves the deliverable back to Submitted for review.",
  requiresComment: false,
  targetState: "submitted",
};

function outcomesForStatus(
  status: DeliverableDerivedLifecycleStatus,
): Outcome[] {
  switch (status) {
    case "submitted":
    case "under-review":
      return [
        acceptOutcome("accept"),
        conditionallyAcceptOutcome,
        failAcceptanceOutcome,
        rejectOutcome,
      ];
    case "conditionally-accepted":
      return [
        acceptOutcome("mark-accepted"),
        returnToReviewOutcome,
        sendBackToSupplierOutcome,
        failAcceptanceOutcome,
        rejectOutcome,
      ];
    case "awaiting-resubmission":
      return [supplierResubmitsOutcome];
    case "accepted":
    case "rejected":
    case "closed":
      return [];
  }
}

const TERMINAL_COPY: Record<"accepted" | "rejected" | "closed", string> = {
  accepted: "This deliverable is accepted. Re-open to record further events.",
  rejected: "This deliverable is rejected. Re-open to record further events.",
  closed: "This deliverable is closed. Re-open to record further events.",
};

export default function AcceptanceGateDialog(props: AcceptanceGateDialogProps) {
  const {
    isOpen,
    onClose,
    title,
    derivedStatus,
    failedAcceptanceCount,
    minDate,
    todayIso,
    onSubmit,
    onReopen,
  } = props;

  // Memoise outcomes so the shared LifecycleTransitionDialog's
  // useEffect (which depends on outcomes' reference identity) does not
  // wipe in-flight selection on every parent rerender. Keyed on
  // derivedStatus AND failedAcceptanceCount: today the failAcceptance
  // outcome's softWarning reads ctx.failedAcceptanceCount at call time
  // (the count is not baked into the closure), so eslint flags
  // failedAcceptanceCount as unnecessary — but the defensive key keeps
  // the invariant true if the closure semantics ever change. Lint
  // suppression is intentional and load-bearing.
  const outcomes = useMemo(
    () => outcomesForStatus(derivedStatus),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [derivedStatus, failedAcceptanceCount],
  );
  const contextSuffix =
    failedAcceptanceCount > 0
      ? `${failedAcceptanceCount} prior failed acceptance${failedAcceptanceCount === 1 ? "" : "s"}`
      : undefined;

  const isTerminal =
    derivedStatus === "accepted" ||
    derivedStatus === "rejected" ||
    derivedStatus === "closed";

  return (
    <LifecycleTransitionDialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      currentStatusLabel={derivedStatus}
      contextSuffix={contextSuffix}
      outcomes={outcomes}
      terminalCopy={isTerminal ? TERMINAL_COPY[derivedStatus] : undefined}
      reopenAction={
        isTerminal ? { label: "Re-open", onConfirm: onReopen } : undefined
      }
      minDate={minDate}
      todayIso={todayIso}
      softWarningCtx={{ failedAcceptanceCount }}
      onSubmit={(outcome, payload) =>
        onSubmit(outcome.targetState as DeliverableLifecycleTarget, payload)
      }
    />
  );
}
